import { authService } from '@/features/authorization/api/authService'
import { useAuthComplete } from '@/features/authorization/hooks/useAuthComplete'
import { routes } from '@/pages/routes'
import { Button, Text } from '@/ui-kit'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type VerificationState = 'loading' | 'expired' | 'error'

export const AuthPage = () => {
  const [searchParams] = useSearchParams()
  const { complete } = useAuthComplete()
  const [state, setState] = useState<VerificationState>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    let cancelled = false

    const authenticate = async () => {
      const token = searchParams.get('token')
      const inviteCode = searchParams.get('invite') || undefined

      if (!token) {
        setState('error')
        setErrorMessage('Missing verification token')
        return
      }

      try {
        const { token: jwt } = await authService.verifyMagicLink(token)
        if (!cancelled) {
          await complete(jwt, inviteCode)
        }
      } catch (err) {
        if (!cancelled) {
          const axiosError = err as AxiosError<{ message?: string }>
          if (axiosError.response?.status === 400) {
            setState('expired')
            setErrorMessage('This link has expired or is invalid. Please request a new one.')
          } else {
            setState('error')
            setErrorMessage(axiosError.response?.data?.message || 'An error occurred during verification.')
          }
        }
      }
    }

    authenticate()

    return () => {
      cancelled = true
    }
  }, [])

  const handleBackToLogin = useCallback(() => {
    window.location.replace(routes.login.path)
  }, [])

  return (
    <>
      {state === 'loading' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            Verifying...
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">Please wait while we verify your login link.</Text>
        </>
      )}

      {state === 'expired' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            Link Expired
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">{errorMessage}</Text>
          <Button className="mt-4" onClick={handleBackToLogin}>
            <span className="colorWhite">Back to login</span>
          </Button>
        </>
      )}

      {state === 'error' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            Verification Failed
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">{errorMessage}</Text>
          <Button className="mt-4" onClick={handleBackToLogin}>
            <span className="colorWhite">Back to login</span>
          </Button>
        </>
      )}
    </>
  )
}

export default AuthPage
